import React, { useEffect, useState, useRef } from 'react';
import * as Icons from 'lucide-react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { useToast } from '../../hooks/use-toast';
import { useApp } from '../../context/AppContext';
import { api } from '../../api';
import { generateReportPDF } from '../../utils/pdf';
import LiveMap from '../shared/LiveMap';
import InventoryRecorder from '../InventoryRecorder';

const ContractorDashboard = () => {
  const { user } = useApp();
  const { toast } = useToast();
  const [jobs, setJobs] = useState([]);
  const [active, setActive] = useState(null);
  const [summary, setSummary] = useState('');
  const [aiBusy, setAiBusy] = useState(false);
  const [recorderJob, setRecorderJob] = useState(null);

  const load = () => api.get('/jobs').then((r) => setJobs(r.data));
  useEffect(() => { load(); const t = setInterval(load, 8000); return () => clearInterval(t); }, []);

  // Job to track: assigned to me and tracking is active (from the moment I accept).
  const trackJob = jobs.find((j) => j.assigned_contractor_id === user.contractor_id && j.tracking_active && ['accepted', 'assigned', 'scheduled', 'in_progress'].includes(j.status));
  const trackJobId = trackJob?.id;

  // Share the contractor's REAL device GPS while a job is being tracked (Uber-style).
  const geoErrShown = useRef(false);
  const lastPos = useRef(null);
  useEffect(() => {
    if (!trackJobId) return;
    if (!('geolocation' in navigator)) { toast({ title: 'GPS unavailable', description: 'This device does not support location.' }); return; }
    let last = 0;
    const post = (lat, lng) => api.post(`/jobs/${trackJobId}/location`, { lat, lng }).then(load).catch(() => {});
    const watchId = navigator.geolocation.watchPosition(
      (pos) => {
        lastPos.current = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        const now = Date.now();
        if (now - last < 5000) return; // throttle to every 5s on movement
        last = now;
        post(pos.coords.latitude, pos.coords.longitude);
      },
      (err) => { if (!geoErrShown.current) { geoErrShown.current = true; toast({ title: 'Location permission needed', description: 'Allow location so your manager can track you en-route.' }); } },
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 20000 }
    );
    // Heartbeat: re-post last known position every 30s so tracking stays fresh when stationary.
    const beat = setInterval(() => { if (lastPos.current) post(lastPos.current.lat, lastPos.current.lng); }, 30000);
    return () => { navigator.geolocation.clearWatch(watchId); clearInterval(beat); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trackJobId]);

  const act = async (id, action, body) => {
    try { await api.post(`/jobs/${id}/${action}`, body || {}); toast({ title: `Job ${action}ed` }); await load(); }
    catch (e) { toast({ title: 'Action failed', description: e?.response?.data?.detail || '' }); }
  };

  const submitComplete = async (job) => {
    await act(job.id, 'complete', { summary });
    setActive(null); setSummary('');
  };

  const genAI = async (job) => {
    setAiBusy(true);
    try { const r = await api.post('/ai/summary', { notes: summary || 'Standard inspection, property in good condition.', service: job.service_name, address: job.property_address }); setSummary(r.data.summary); }
    catch (e) { toast({ title: 'AI generation failed' }); } finally { setAiBusy(false); }
  };

  const broadcast = jobs.filter((j) => j.status === 'broadcasted');
  const activeJobs = jobs.filter((j) => ['scheduled', 'in_progress', 'assigned', 'accepted'].includes(j.status));
  const done = jobs.filter((j) => j.group === 'completed');
  const earnings = jobs.reduce((a, j) => a + (j.assigned_contractor_id === user.contractor_id && j.group === 'completed' ? (j.price || 0) : 0), 0);

  return (
    <div>
      <div className="mb-8"><h1 className="font-display font-extrabold text-2xl text-[#102d55]">Contractor dashboard</h1><p className="text-muted-foreground">{user.name} · your jobs & inspection queue</p></div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {[{ i: 'Radio', l: 'Available to accept', v: broadcast.length, t: 'bg-amber-50 text-amber-600' }, { i: 'ClipboardCheck', l: 'Active', v: activeJobs.length, t: 'bg-blue-50 text-blue-600' }, { i: 'CheckCircle2', l: 'Completed', v: done.length, t: 'bg-emerald-50 text-emerald-600' }, { i: 'PoundSterling', l: 'Earnings', v: '£' + earnings, t: 'bg-[#eaf1fb] text-[#1c63c4]' }].map((s) => { const C = Icons[s.i]; return <div key={s.l} className="bg-white rounded-2xl border border-border p-5"><div className={`h-10 w-10 rounded-xl flex items-center justify-center mb-3 ${s.t}`}><C className="h-5 w-5" /></div><div className="text-2xl font-display font-extrabold text-[#102d55]">{s.v}</div><div className="text-sm text-muted-foreground">{s.l}</div></div>; })}
      </div>

      {trackJob && (
        <div className="bg-white rounded-2xl border border-border p-5 mb-8">
          <div className="flex items-center gap-2 mb-3"><Icons.Navigation className="h-4 w-4 text-emerald-600" /><h2 className="font-display font-semibold text-[#102d55]">Sharing your live location</h2><span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse ml-1" /></div>
          <p className="text-sm text-muted-foreground mb-3">
            {trackJob.contractor_location
              ? <>Tracking active for <span className="font-medium text-[#102d55]">{trackJob.service_name} · {trackJob.id}</span> → {trackJob.property_address}. Your manager can see you en-route.</>
              : <>Acquiring your GPS location for <span className="font-medium text-[#102d55]">{trackJob.service_name} · {trackJob.id}</span>… allow location access when prompted.</>}
          </p>
          <LiveMap property={trackJob.property_coords} contractor={trackJob.contractor_location} history={trackJob.location_history} height={240} />
        </div>
      )}

      {broadcast.length > 0 && (<><h2 className="font-display font-semibold text-lg text-[#102d55] mb-4">New jobs to accept</h2>
      <div className="space-y-3 mb-10">{broadcast.map((job) => (
        <div key={job.id} className="bg-white rounded-2xl border border-amber-200 p-5 flex flex-wrap items-center gap-3 justify-between">
          <div><div className="font-display font-semibold text-[#102d55]">{job.service_name} <span className="text-sm font-normal text-muted-foreground">· {job.id}</span></div><div className="text-sm text-muted-foreground">{job.property_address} · {job.property_size} · {job.scheduled_date}</div></div>
          <div className="flex gap-2"><Button variant="outline" onClick={() => act(job.id, 'reject')}>Decline</Button><Button onClick={() => act(job.id, 'accept')} className="bg-[#102d55] hover:bg-[#0b2244]"><Icons.Check className="h-4 w-4 mr-1" /> Accept</Button></div>
        </div>))}</div></>)}

      <h2 className="font-display font-semibold text-lg text-[#102d55] mb-4">Active jobs</h2>
      <div className="space-y-3 mb-10">
        {activeJobs.length === 0 && <p className="text-muted-foreground">No active jobs.</p>}
        {activeJobs.map((job) => (
          <div key={job.id} className="bg-white rounded-2xl border border-border p-5">
            <div className="flex flex-wrap items-start justify-between gap-3"><div><div className="font-display font-semibold text-[#102d55]">{job.service_name} <span className="text-sm font-normal text-muted-foreground">· {job.id}</span></div><div className="text-sm text-muted-foreground">{job.property_address} · {job.property_size}</div></div><span className="px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 capitalize">{job.status.replace(/_/g, ' ')}</span></div>
            {active === job.id ? (
              <div className="mt-4 pt-4 border-t border-border">
                <Textarea value={summary} onChange={(e) => setSummary(e.target.value)} rows={4} placeholder="Inspection findings / report summary…" />
                <div className="flex flex-wrap gap-2 mt-3"><Button variant="ghost" onClick={() => { setActive(null); setSummary(''); }}>Cancel</Button><Button variant="outline" disabled={aiBusy} onClick={() => genAI(job)}><Icons.Sparkles className="h-4 w-4 mr-1.5" /> {aiBusy ? 'Generating…' : 'AI draft'}</Button><Button onClick={() => submitComplete(job)} className="bg-[#102d55] hover:bg-[#0b2244]"><Icons.Upload className="h-4 w-4 mr-1.5" /> Submit & complete</Button></div>
              </div>
            ) : (
              <div className="mt-4 flex gap-2">{job.status === 'scheduled' && <Button onClick={() => act(job.id, 'start')} className="bg-[#102d55] hover:bg-[#0b2244]"><Icons.Play className="h-4 w-4 mr-1.5" /> Start job</Button>}{job.status === 'in_progress' && <Button onClick={() => setRecorderJob(job)} className="bg-[#102d55] hover:bg-[#0b2244]"><Icons.ClipboardCheck className="h-4 w-4 mr-1.5" /> Mark inspection complete</Button>}<Button variant="outline" onClick={() => act(job.id, 'cancel')}>Cancel</Button></div>
            )}
          </div>
        ))}
      </div>

      <h2 className="font-display font-semibold text-lg text-[#102d55] mb-4">Job history</h2>
      <div className="space-y-3">
        {done.length === 0 && <p className="text-muted-foreground">No completed jobs yet.</p>}
        {done.map((job) => (<div key={job.id} className="bg-white rounded-2xl border border-border p-5 flex flex-wrap items-center gap-3 justify-between"><div className="min-w-0"><div className="font-medium text-[#102d55]">{job.service_name} · {job.id}</div><div className="text-sm text-muted-foreground truncate">{job.property_address}</div></div><Button size="sm" variant="outline" onClick={() => generateReportPDF(job)}><Icons.FileText className="h-4 w-4 mr-1" /> Report</Button></div>))}
      </div>

      {recorderJob && <InventoryRecorder job={recorderJob} onClose={() => { setRecorderJob(null); load(); }} onCompleted={() => { setRecorderJob(null); load(); }} />}
    </div>
  );
};

export default ContractorDashboard;
